import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  serverTimestamp,
  DocumentSnapshot,
  QueryConstraint,
  writeBatch,
  deleteDoc,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import {
  AuditLog,
  AuditLogFirestore,
  AuditLogFilters,
  AdminActionType,
} from '../types/admin';

/**
 * Audit Log Service
 * Handles logging and retrieval of all admin actions for accountability and auditing
 *
 * This service provides methods for:
 * - Logging admin actions (user approvals, role changes, deletions, etc.)
 * - Querying audit logs with filters
 * - Retrieving user-specific activity history
 * - Exporting audit logs
 *
 * All admin actions should be logged through this service to maintain
 * a complete audit trail of system changes.
 */
export class AuditLogService {
  /**
   * Log an admin action to the audit trail
   * @param logData - Action details to log
   */
  static async logAction(logData: {
    userId: string;
    action: AdminActionType;
    targetUserId?: string;
    contentType?: 'person' | 'photo' | 'comment' | 'relationship' | 'document' | 'memory' | 'note' | 'research-note';
    contentId?: string;
    details: Record<string, any>;
  }): Promise<void> {
    try {
      // Get admin user info
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.warn('No authenticated user for audit log');
        return;
      }

      // Get user agent and IP (IP would require server-side in production)
      const userAgent = navigator.userAgent;

      const auditLogData: Omit<AuditLogFirestore, 'id'> = {
        userId: logData.userId,
        userEmail: currentUser.email || 'unknown',
        userName: currentUser.displayName || 'Unknown User',
        action: logData.action,
        targetUserId: logData.targetUserId,
        targetUserEmail: logData.details.userEmail || undefined,
        contentType: logData.contentType,
        contentId: logData.contentId,
        details: logData.details,
        timestamp: serverTimestamp() as Timestamp,
        userAgent,
      };

      // Remove undefined fields before saving to Firestore
      const cleanedData = this.removeUndefinedFields(auditLogData);

      await addDoc(collection(db, 'auditLogs'), cleanedData);

      console.log('Audit log created:', logData.action);
    } catch (error) {
      console.error('Error logging audit action:', error);
      // Don't throw error - audit logging should not break the main operation
    }
  }

  /**
   * Get audit logs with optional filtering and pagination
   * @param filters - Optional filters for querying logs
   * @param pageSize - Number of logs per page (default: 50)
   * @param lastDoc - Last document from previous page (for pagination)
   * @returns Array of audit logs and pagination info
   */
  static async getAuditLogs(
    filters?: AuditLogFilters,
    pageSize: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<{ logs: AuditLog[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
    try {
      const constraints: QueryConstraint[] = [];

      // Add filters
      if (filters?.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }

      if (filters?.targetUserId) {
        constraints.push(where('targetUserId', '==', filters.targetUserId));
      }

      if (filters?.action) {
        constraints.push(where('action', '==', filters.action));
      }

      if (filters?.contentType) {
        constraints.push(where('contentType', '==', filters.contentType));
      }

      if (filters?.startDate) {
        constraints.push(where('timestamp', '>=', Timestamp.fromDate(filters.startDate)));
      }

      if (filters?.endDate) {
        constraints.push(where('timestamp', '<=', Timestamp.fromDate(filters.endDate)));
      }

      // Add ordering
      const orderByField = filters?.orderBy || 'timestamp';
      const orderDirection = filters?.orderDirection || 'desc';
      constraints.push(orderBy(orderByField, orderDirection));

      // Add pagination
      const limitCount = filters?.limit || pageSize;
      constraints.push(limit(limitCount + 1)); // Fetch one extra to check if there are more

      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, 'auditLogs'), ...constraints);
      const snapshot = await getDocs(q);

      const logs: AuditLog[] = [];
      let newLastDoc: DocumentSnapshot | null = null;
      let hasMore = false;

      snapshot.docs.forEach((doc, index) => {
        if (index < limitCount) {
          const data = doc.data();
          logs.push({
            id: doc.id,
            userId: data.userId,
            userEmail: data.userEmail,
            userName: data.userName,
            action: data.action,
            targetUserId: data.targetUserId,
            targetUserEmail: data.targetUserEmail,
            contentType: data.contentType,
            contentId: data.contentId,
            details: data.details,
            timestamp: data.timestamp?.toDate(),
            ipAddress: data.ipAddress,
            userAgent: data.userAgent,
          });
          newLastDoc = doc;
        } else {
          hasMore = true;
        }
      });

      return { logs, lastDoc: newLastDoc, hasMore };
    } catch (error) {
      console.error('Error getting audit logs:', error);
      throw new Error('Failed to retrieve audit logs');
    }
  }

  /**
   * Get audit logs for a specific user
   * @param userId - User ID to get logs for
   * @param limitCount - Maximum number of logs to return
   * @returns Array of audit logs
   */
  static async getUserAuditLogs(userId: string, limitCount: number = 50): Promise<AuditLog[]> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      const logs: AuditLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          userId: data.userId,
          userEmail: data.userEmail,
          userName: data.userName,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          contentType: data.contentType,
          contentId: data.contentId,
          details: data.details,
          timestamp: data.timestamp?.toDate(),
          ipAddress: data.ipAddress,
          userAgent: data.userAgent,
        });
      });

      return logs;
    } catch (error) {
      console.error('Error getting user audit logs:', error);
      throw new Error('Failed to retrieve user audit logs');
    }
  }

  /**
   * Get audit logs for actions affecting a specific user
   * @param targetUserId - Target user ID to get logs for
   * @param limitCount - Maximum number of logs to return
   * @returns Array of audit logs
   */
  static async getTargetUserAuditLogs(
    targetUserId: string,
    limitCount: number = 50
  ): Promise<AuditLog[]> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        where('targetUserId', '==', targetUserId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      const logs: AuditLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          userId: data.userId,
          userEmail: data.userEmail,
          userName: data.userName,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          contentType: data.contentType,
          contentId: data.contentId,
          details: data.details,
          timestamp: data.timestamp?.toDate(),
          ipAddress: data.ipAddress,
          userAgent: data.userAgent,
        });
      });

      return logs;
    } catch (error) {
      console.error('Error getting target user audit logs:', error);
      throw new Error('Failed to retrieve target user audit logs');
    }
  }

  /**
   * Get recent audit logs
   * @param limitCount - Number of recent logs to return (default: 20)
   * @returns Array of recent audit logs
   */
  static async getRecentLogs(limitCount: number = 20): Promise<AuditLog[]> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      const logs: AuditLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          userId: data.userId,
          userEmail: data.userEmail,
          userName: data.userName,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          contentType: data.contentType,
          contentId: data.contentId,
          details: data.details,
          timestamp: data.timestamp?.toDate(),
          ipAddress: data.ipAddress,
          userAgent: data.userAgent,
        });
      });

      return logs;
    } catch (error) {
      console.error('Error getting recent logs:', error);
      throw new Error('Failed to retrieve recent logs');
    }
  }

  /**
   * Get audit log statistics
   * @param startDate - Start date for statistics (optional)
   * @param endDate - End date for statistics (optional)
   * @returns Statistics about audit logs
   */
  static async getAuditLogStats(startDate?: Date, endDate?: Date): Promise<{
    totalLogs: number;
    logsByAction: Record<AdminActionType, number>;
    logsByUser: Record<string, number>;
    recentActivity: AuditLog[];
  }> {
    try {
      const constraints: QueryConstraint[] = [];

      if (startDate) {
        constraints.push(where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }

      if (endDate) {
        constraints.push(where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      constraints.push(orderBy('timestamp', 'desc'));

      const q = query(collection(db, 'auditLogs'), ...constraints);
      const snapshot = await getDocs(q);

      const logsByAction: Record<string, number> = {};
      const logsByUser: Record<string, number> = {};
      const recentActivity: AuditLog[] = [];

      snapshot.docs.forEach((doc, index) => {
        const data = doc.data();

        // Count by action
        logsByAction[data.action] = (logsByAction[data.action] || 0) + 1;

        // Count by user
        logsByUser[data.userId] = (logsByUser[data.userId] || 0) + 1;

        // Get recent activity (last 10)
        if (index < 10) {
          recentActivity.push({
            id: doc.id,
            userId: data.userId,
            userEmail: data.userEmail,
            userName: data.userName,
            action: data.action,
            targetUserId: data.targetUserId,
            targetUserEmail: data.targetUserEmail,
            contentType: data.contentType,
            contentId: data.contentId,
            details: data.details,
            timestamp: data.timestamp?.toDate(),
            ipAddress: data.ipAddress,
            userAgent: data.userAgent,
          });
        }
      });

      return {
        totalLogs: snapshot.size,
        logsByAction: logsByAction as Record<AdminActionType, number>,
        logsByUser,
        recentActivity,
      };
    } catch (error) {
      console.error('Error getting audit log stats:', error);
      throw new Error('Failed to retrieve audit log statistics');
    }
  }

  /**
   * Search audit logs by keyword
   * @param keyword - Keyword to search for
   * @param limitCount - Maximum number of results
   * @returns Array of matching audit logs
   */
  static async searchAuditLogs(keyword: string, limitCount: number = 50): Promise<AuditLog[]> {
    try {
      // Firestore doesn't support full-text search, so we'll fetch recent logs
      // and filter in memory (not optimal for large datasets)
      const q = query(
        collection(db, 'auditLogs'),
        orderBy('timestamp', 'desc'),
        limit(500) // Fetch more to search through
      );

      const snapshot = await getDocs(q);
      const keywordLower = keyword.toLowerCase();

      const logs: AuditLog[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();

        // Search in relevant fields
        const searchableText = [
          data.userName,
          data.userEmail,
          data.action,
          data.targetUserEmail,
          JSON.stringify(data.details),
        ]
          .join(' ')
          .toLowerCase();

        if (searchableText.includes(keywordLower)) {
          logs.push({
            id: doc.id,
            userId: data.userId,
            userEmail: data.userEmail,
            userName: data.userName,
            action: data.action,
            targetUserId: data.targetUserId,
            targetUserEmail: data.targetUserEmail,
            contentType: data.contentType,
            contentId: data.contentId,
            details: data.details,
            timestamp: data.timestamp?.toDate(),
            ipAddress: data.ipAddress,
            userAgent: data.userAgent,
          });

          // Stop when we have enough results
          if (logs.length >= limitCount) {
            return;
          }
        }
      });

      return logs;
    } catch (error) {
      console.error('Error searching audit logs:', error);
      throw new Error('Failed to search audit logs');
    }
  }

  /**
   * Export audit logs to CSV format
   * @param filters - Optional filters for export
   * @returns CSV string of audit logs
   */
  static async exportAuditLogs(filters?: AuditLogFilters): Promise<string> {
    try {
      const { logs } = await this.getAuditLogs(filters, 10000); // Export up to 10,000 logs

      // CSV header
      const header = [
        'ID',
        'Timestamp',
        'Admin User',
        'Admin Email',
        'Action',
        'Target User',
        'Target Email',
        'Content Type',
        'Content ID',
        'Details',
      ].join(',');

      // CSV rows
      const rows = logs.map((log) => {
        return [
          log.id,
          log.timestamp?.toISOString() || '',
          `"${log.userName}"`,
          log.userEmail,
          log.action,
          log.targetUserId || '',
          log.targetUserEmail || '',
          log.contentType || '',
          log.contentId || '',
          `"${JSON.stringify(log.details).replace(/"/g, '""')}"`, // Escape quotes
        ].join(',');
      });

      return [header, ...rows].join('\n');
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw new Error('Failed to export audit logs');
    }
  }

  /**
   * Get action count for a specific admin user
   * @param userId - Admin user ID
   * @param action - Optional specific action to count
   * @returns Number of actions performed
   */
  static async getAdminActionCount(userId: string, action?: AdminActionType): Promise<number> {
    try {
      const constraints: QueryConstraint[] = [where('userId', '==', userId)];

      if (action) {
        constraints.push(where('action', '==', action));
      }

      const q = query(collection(db, 'auditLogs'), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.size;
    } catch (error) {
      console.error('Error getting admin action count:', error);
      return 0;
    }
  }

  /**
   * Get action count for a specific target user (actions performed ON them)
   * @param targetUserId - Target user ID
   * @returns Number of actions affecting this user
   */
  static async getTargetActionCount(targetUserId: string): Promise<number> {
    try {
      const q = query(
        collection(db, 'auditLogs'),
        where('targetUserId', '==', targetUserId)
      );
      const snapshot = await getDocs(q);

      return snapshot.size;
    } catch (error) {
      console.error('Error getting target action count:', error);
      return 0;
    }
  }

  /**
   * Remove undefined fields from an object to prevent Firestore errors
   * @param obj - Object to clean
   * @returns Object with undefined fields removed
   */
  private static removeUndefinedFields<T extends Record<string, any>>(obj: T): Partial<T> {
    const cleaned: Partial<T> = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
        cleaned[key] = obj[key];
      }
    }

    return cleaned;
  }

  /**
   * Clean up old audit logs (for maintenance)
   * @param daysToKeep - Number of days of logs to keep (default: 90)
   * @returns Number of logs deleted
   */
  static async cleanupOldLogs(daysToKeep: number = 90): Promise<number> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

      const q = query(
        collection(db, 'auditLogs'),
        where('timestamp', '<', Timestamp.fromDate(cutoffDate))
      );

      const snapshot = await getDocs(q);

      // Delete in batches (Firestore has a 500 doc limit per batch)
      const batch = [];
      let deleteCount = 0;

      for (const docSnap of snapshot.docs) {
        batch.push(docSnap.ref);
        deleteCount++;

        // If we have 500 docs, commit the batch
        if (batch.length === 500) {
          const batchWrite = writeBatch(db);
          batch.forEach((ref) => batchWrite.delete(ref));
          await batchWrite.commit();
          batch.length = 0; // Clear the array
        }
      }

      // Commit remaining docs
      if (batch.length > 0) {
        const batchWrite = writeBatch(db);
        batch.forEach((ref) => batchWrite.delete(ref));
        await batchWrite.commit();
      }

      console.log(`Cleaned up ${deleteCount} old audit logs`);
      return deleteCount;
    } catch (error) {
      console.error('Error cleaning up old logs:', error);
      throw new Error('Failed to clean up old audit logs');
    }
  }
}

// Export singleton instance
export const auditLogService = AuditLogService;
